import type { StepConfig } from "@/signup/stepperStore";
import { useStepperStore } from "@/signup/stepperStore";
import { defineStore } from "pinia";
import { computed, markRaw, ref, watch } from "vue";

// Import components for Transfer Flow
import TransferFiAccountDetails from "@/signup/fi/flows/transfer/FiAccountDetails.vue";
import TransferFiContractReview from "@/signup/fi/flows/transfer/FiContractReview.vue";

// Import components for Authorization Flow (will be created in next step)
import AuthorizationFiAccountDetails from "@/signup/fi/flows/authorization/FiAccountDetails.vue";
import AuthorizationFiContractReview from "@/signup/fi/flows/authorization/FiContractReview.vue";

import { useValidators } from "@/composables/useValidators";
import { useSignupStore } from "@/signup/signup";
import { useVuelidate } from "@vuelidate/core";
import { useRouteParams } from "@vueuse/router";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { RouteQueryAndHash } from "vue-router";

// Define flow structure for internal use that supports a map of steps
export interface FlowStepMap {
  [key: string]: {
    id: string;
    title: string;
    component: any;
    disabled?: boolean;
  };
}

// Define flow type
export interface FlowConfig {
  id: string;
  steps: FlowStepMap;
  defaultStep: string;
}

// Flow configurations
export const TRANSFER_FLOW: FlowConfig = {
  id: "transfer",
  steps: {
    "contact-details": {
      id: "contact-details",
      title: "signup.navsteps.fi.contact_details",
      component: markRaw(TransferFiAccountDetails),
    },
    "contract-review": {
      id: "contract-review",
      title: "signup.navsteps.fi.contract_review",
      component: markRaw(TransferFiContractReview),
    },
  },
  defaultStep: "contact-details",
};

// Authorization Flow configuration
export const AUTHORIZATION_FLOW: FlowConfig = {
  id: "authorization",
  steps: {
    "contact-details": {
      id: "contact-details",
      title: "signup.navsteps.fi.contact_details",
      component: markRaw(AuthorizationFiAccountDetails),
    },
    "contract-review": {
      id: "contract-review",
      title: "signup.navsteps.fi.contract_review",
      component: markRaw(AuthorizationFiContractReview),
    },
  },
  defaultStep: "contact-details",
};

// Map flow IDs to configurations
export const FLOWS: Record<string, FlowConfig> = {
  transfer: TRANSFER_FLOW,
  authorization: AUTHORIZATION_FLOW,
};

// Helper function to convert our FlowStepMap to StepConfig[] for the stepper store
function flowStepsToStepperConfig(
  flowSteps: FlowStepMap,
  t: any
): StepConfig[] {
  return Object.values(flowSteps).map((step) => ({
    id: step.id,
    title: t(step.title),
    component: step.component,
    disabled: step.disabled,
  }));
}

// Define interfaces for step configurations
interface StepValidator {
  (): boolean | Promise<boolean>;
}

interface StepNextHandler {
  (query?: RouteQueryAndHash["query"]): Promise<boolean | void>;
}

interface ValidationConfig {
  rules: Record<string, any>;
  data: () => Record<string, any>;
  watchTargets?: (() => any)[];
}

interface StepBehavior {
  validator?: StepValidator;
  nextHandler?: StepNextHandler;
  watchDependencies?: (() => any)[];
  validation?: ValidationConfig;
}

export const useFISignupStore = defineStore("signup_fi", () => {
  const { t } = useI18n();

  // Error handler will be handled by components using this store

  const isLoading = ref(false); // Loading state

  // Current flow
  const currentFlowId = useRouteParams<string>("flow");

  // Get the generic stepper store
  const stepperStore = useStepperStore();
  const signupStore = useSignupStore();
  const { account, company } = storeToRefs(signupStore);

  // Get Finnish validators
  const {
    required,
    personalName,
    phoneNumber,
    finnishPersonalID,
    finnishIDorBusinessID,
    isValidVAT,
  } = useValidators(t);

  // Store Vuelidate instances for steps that need validation
  const stepValidators = ref<Record<string, any>>({});

  // Dynamic step configuration registry
  const stepConfigurations = ref<Record<string, StepBehavior>>({
    // Contact details step configuration
    "contact-details": {
      validation: {
        rules: {
          name: { required, personalName },
          phone_number: { required, phoneNumber },
          registry_code: { required, finnishPersonalID },
          company: {
            name: { required },
            registry_code: { required, finnishIDorBusinessID },
            vat: { required, isValidVAT },
            address: { required },
          },
        },
        data: () => ({
          name: account.value?.name || "",
          phone_number: account.value?.phone_number || "",
          registry_code: account.value?.registry_code || "",
          company: {
            name: company.value?.name || "",
            registry_code: company.value?.registry_code || "",
            address: company.value?.address || "",
            vat: company.value?.vat || "",
          },
        }),
        watchTargets: [() => account.value, () => company.value],
      },
      validator: async () => {
        const v$ = stepValidators.value["contact-details"];
        if (!v$) return false;

        const result = await v$.$validate();
        const hasRequiredFields = Boolean(
          account.value?.name &&
          account.value?.phone_number &&
          account.value?.registry_code &&
          company.value?.name &&
          company.value?.registry_code &&
          company.value?.address
        );
        return result && hasRequiredFields;
      },
      nextHandler: async (query?: RouteQueryAndHash["query"]) => {
        try {
          isLoading.value = true;

          if (!formValidationState.value["contact-details"]) {
            return false;
          }

          const { company_id } = await signupStore.saveDraft(
            "/api/fi/update-company"
          );

          await stepperStore.next({ ...(query ?? {}), company_id });
        } catch (error) {
          console.error("Error submitting contact details:", error);
          throw error;
        } finally {
          isLoading.value = false;
        }
      },
    },

    // Contract review step configuration
    "contract-review": {
      validation: {
        rules: {
          company_id: { required },
        },
        data: () => ({
          company_id: company.value?.id,
        }),
        watchTargets: [() => company.value],
      },
      validator: () => {
        if (!company.value?.id) {
          return false;
        }

        return true;
      },
    },
  });

  // Initialize validation for steps that need it
  function initializeStepValidation() {
    Object.entries(stepConfigurations.value).forEach(([stepId, config]) => {
      if (config.validation) {
        // Create Vuelidate instance for this step
        const v$ = useVuelidate(
          computed(() => config.validation?.rules),
          computed(() => config.validation?.data())
        );
        stepValidators.value[stepId] = v$;

        // Set up watchers for validation
        if (config.validation.watchTargets) {
          watch(
            config.validation.watchTargets,
            async () => {
              if (stepperStore.currentStepId === stepId) {
                await validateStep(stepId);
              }
            },
            { immediate: true, deep: true }
          );
        }

        // Watch the Vuelidate instance itself
        watch(
          v$,
          async () => {
            if (stepperStore.currentStepId === stepId) {
              await validateStep(stepId);
            }
          },
          { deep: true }
        );
      }
    });
  }

  // Validate a specific step using its validator function
  async function validateStep(stepId: string) {
    const config = stepConfigurations.value[stepId];
    let isValid = false;

    if (config && config.validator) {
      isValid = await config.validator();
    }

    formValidationState.value[stepId] = isValid;

    if (stepperStore.currentStepId === stepId) {
      stepperStore.setCurrentStepValidity(isValid);
    }
  }

  // Initialize with the current flow from route
  async function initialize(flowId: string) {
    currentFlowId.value = flowId;

    if (!FLOWS[flowId]) {
      console.warn(`Flow '${flowId}' not found`);
      return;
    }

    stepperStore.initialize({
      steps: flowStepsToStepperConfig(FLOWS[flowId].steps, t),
      defaultStep: FLOWS[flowId].defaultStep,
      basePath: `/signup/fi/${flowId}`,
    });

    initializeStepValidation();

    // Run initial validation for the current step
    const currentStepId = stepperStore.currentStepId;
    if (currentStepId) {
      await validateStep(currentStepId);
      if (formValidationState.value[currentStepId] !== undefined) {
        stepperStore.setCurrentStepValidity(
          formValidationState.value[currentStepId]
        );
      }
    }
  }

  // Validation state
  const formValidationState = ref<Record<string, boolean>>({});

  // Watch for step changes to update current step validity
  watch(
    () => stepperStore.currentStepId,
    async (stepId) => {
      if (!stepId) return;

      // First validate the new step
      await validateStep(stepId);

      // Then update the stepper validity with the result
      if (formValidationState.value[stepId] !== undefined) {
        stepperStore.setCurrentStepValidity(formValidationState.value[stepId]);
      }
    }
  );

  // Check if a step is accessible
  const isStepAccessible = computed(() => {
    return (stepId: string): boolean => {
      return stepperStore.isStepAccessible(stepId);
    };
  });

  // Handle moving to a specific step
  async function goto(stepId: string) {
    const currentStepId = stepperStore.currentStepId;

    // If already on the step, do nothing
    if (stepId === currentStepId) return;

    // Find step indexes to determine direction
    const targetIndex = stepperStore.enabledSteps.findIndex(
      (step) => step.id === stepId
    );
    const currentIndex = stepperStore.enabledSteps.findIndex(
      (step) => step.id === currentStepId
    );

    // Check if we can navigate to this step
    if (!isStepAccessible.value(stepId)) return false;

    // If going forward by one step, we need to save the current step data
    if (targetIndex === currentIndex + 1) {
      // Only proceed if current step is valid
      if (!stepperStore.isCurrentStepValid) {
        console.warn("Cannot proceed to next step: current step is not valid");
        return false;
      }

      // Handle the step transition using the configured next handler
      return await next();
    } else {
      // For backwards navigation, just go directly
      return await stepperStore.goto(stepId);
    }
  }

  // Move to the next step - calls the appropriate handler based on current step
  async function next(query?: RouteQueryAndHash["query"]) {
    if (!stepperStore.hasNextStep || !stepperStore.nextStep) {
      return false;
    }

    // Get current step ID
    const currentStepId = stepperStore.currentStepId;
    const config = stepConfigurations.value[currentStepId];

    // If we have a custom next handler for this step, use it
    if (config && config.nextHandler) {
      return await config.nextHandler(query);
    }

    // Otherwise, just use the default next behavior
    return await stepperStore.next(query);
  }

  return {
    // State
    currentFlowId,
    isLoading,
    stepConfigurations,

    // Expose stepper store properties and methods
    currentStep: computed(() => stepperStore.currentStep),
    enabledSteps: computed(() => stepperStore.enabledSteps),
    hasPreviousStep: computed(() => stepperStore.hasPreviousStep),
    isStepAccessible,
    isCurrentStepValid: computed(() => stepperStore.isCurrentStepValid),

    // Actions
    initialize,
    goto,
    previous: stepperStore.previous,
    next,
  };
});
